import { Injectable } from '@angular/core';
import { Invoices, InvoicesList, Jobs } from "../interfaces";

@Injectable({
  providedIn: 'root'
})
export class XinvoiceGeneratorService {

  constructor() { }

  private xml(value: unknown): string {
    const str = (value ?? '').toString();
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private num(value: unknown, digits = 2): string {
    const normalized = (value ?? 0).toString().replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    const safe = Number.isFinite(parsed) ? parsed : 0;
    return safe.toFixed(digits);
  }

  private normalizeCountry(value: string): string {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return 'DE';
    }
    if (trimmed.toLowerCase() === 'deutschland') {
      return 'DE';
    }
    return trimmed.toUpperCase();
  }

  private normalizeDate(value: string): string {
    const raw = (value || '').trim();
    if (!raw) {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const match = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
    return raw;
  }

  private inferTaxPercent(invoice: Invoices): number {
    const brutto = Number.parseFloat((invoice.brutto ?? 0).toString());
    const netto = Number.parseFloat((invoice.netto ?? 0).toString());
    if (Number.isFinite(brutto) && Number.isFinite(netto) && netto > 0 && brutto >= netto) {
      return Number((((brutto - netto) / netto) * 100).toFixed(2));
    }
    if ((invoice.vat_id || '').toUpperCase().startsWith('DE')) {
      return 19;
    }
    if (this.normalizeCountry(invoice.country) === 'DE') {
      return 19;
    }
    return 0;
  }

  createXInvoice(invoiceID: string, invoice: Invoices, job: Jobs, positions: InvoicesList[], skontoDays: number, skonto: number, staffFirstname: string, staffLastname: string, staffEmail: string, staffPhone: string): string {

    const taxPercent = this.inferTaxPercent(invoice);
    const taxCategoryId = taxPercent > 0 ? 'S' : 'Z';

    // Summe der Positionszeilen ist die autoritative Nettobasis (BR-CO-10)
    const lineSum = positions.reduce((acc, p) => {
      const qty = Number.parseFloat(this.num(p.number_of_pieces, 2));
      const price = Number.parseFloat(this.num(p.selling_price, 2));
      return acc + qty * price;
    }, 0);
    const exclusiveAmount = Number.parseFloat(lineSum.toFixed(2));
    const taxAmount = Number.parseFloat((exclusiveAmount * taxPercent / 100).toFixed(2));
    const inclusiveAmount = Number.parseFloat((exclusiveAmount + taxAmount).toFixed(2));

    let invoiceText = `<?xml version="1.0" encoding="UTF-8"?>
    <ubl:Invoice xmlns:ubl="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
             xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
             xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
        <cbc:CustomizationID>urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0</cbc:CustomizationID>
        <cbc:ProfileID>urn:fdc:peppol.eu:2017:poacc:billing:01:1.0</cbc:ProfileID>`

    const invoicePositions = positions.map(product => {
      const quantity = Number.parseFloat(this.num(product.number_of_pieces, 2));
      const unitPrice = Number.parseFloat(this.num(product.selling_price, 2));
      const productTotal = Number.parseFloat(this.num(quantity * unitPrice, 2));
      const lineId = product.part > 0 ? `${product.position}.${product.part}` : `${product.position}`;
      return `
    <cac:InvoiceLine>
        <cbc:ID>${this.xml(lineId)}</cbc:ID>
        <cbc:InvoicedQuantity unitCode="C62">${this.num(quantity, 2)}</cbc:InvoicedQuantity>
        <cbc:LineExtensionAmount currencyID="EUR">${productTotal.toFixed(2)}</cbc:LineExtensionAmount>
        <cac:Item>
            ${product.serial_nr ? `<cbc:Description>${this.xml(product.serial_nr)}</cbc:Description>` : ''}
            <cbc:Name>${this.xml(product.name)}</cbc:Name>
            <cac:SellersItemIdentification>
                <cbc:ID>${this.xml(product.technical_drawing)}</cbc:ID>
            </cac:SellersItemIdentification>
            <cac:ClassifiedTaxCategory>
                <cbc:ID>${taxCategoryId}</cbc:ID>
                <cbc:Percent>${this.num(taxPercent)}</cbc:Percent>
                <cac:TaxScheme>
                    <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
            </cac:ClassifiedTaxCategory>
        </cac:Item>
        <cac:Price>
            <cbc:PriceAmount currencyID="EUR">${this.num(unitPrice)}</cbc:PriceAmount>
            <cbc:BaseQuantity unitCode="C62">1.00</cbc:BaseQuantity>
            <cac:AllowanceCharge>
                <cbc:ChargeIndicator>false</cbc:ChargeIndicator>
                <cbc:Amount currencyID="EUR">0.00</cbc:Amount>
                <cbc:BaseAmount currencyID="EUR">${this.num(unitPrice)}</cbc:BaseAmount>
            </cac:AllowanceCharge>
        </cac:Price>
    </cac:InvoiceLine>`;
    })

    invoiceText +=
      `<cbc:ID>${this.xml(invoiceID || `${invoice.praefix}.${invoice.number}`)}</cbc:ID>
       <cbc:IssueDate>${this.xml(this.normalizeDate(invoice.dateI))}</cbc:IssueDate>
       <cbc:DueDate>${this.xml(this.normalizeDate(invoice.dateII))}</cbc:DueDate>
       <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>
       <cbc:DocumentCurrencyCode>EUR</cbc:DocumentCurrencyCode>
       <cbc:BuyerReference>${this.xml(job.invoice_leitweg_id || invoice.order_id)}</cbc:BuyerReference>
       <cac:OrderReference>
          <cbc:ID>${this.xml(invoice.order_id)}</cbc:ID>
       </cac:OrderReference>
       <cac:AccountingSupplierParty>
          <cac:Party>
             <cbc:EndpointID schemeID="EM">rechnung@phm-burg.de</cbc:EndpointID>
             <cac:PartyIdentification>
                <cbc:ID>342064</cbc:ID>
             </cac:PartyIdentification>
             <cac:PostalAddress>
                <cbc:StreetName>Kiefernweg 17</cbc:StreetName>
                <cbc:CityName>Burg</cbc:CityName>
                <cbc:PostalZone>39288</cbc:PostalZone>
                <cac:Country>
                   <cbc:IdentificationCode>DE</cbc:IdentificationCode>
                </cac:Country>
             </cac:PostalAddress>
             <cac:PartyTaxScheme>
                <cbc:CompanyID>DE154347770</cbc:CompanyID>
                <cac:TaxScheme>
                   <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
             </cac:PartyTaxScheme>
             <cac:PartyLegalEntity>
                <cbc:RegistrationName>PHM-Industrieanlagen GmbH</cbc:RegistrationName>
                <cbc:CompanyID>HRB2398</cbc:CompanyID>
             </cac:PartyLegalEntity>
             <cac:Contact>
                <cbc:Name>${this.xml(`${staffFirstname} ${staffLastname}`.trim())}</cbc:Name>
                <cbc:Telephone>${this.xml(staffPhone)}</cbc:Telephone>
                <cbc:ElectronicMail>${this.xml(staffEmail)}</cbc:ElectronicMail>
             </cac:Contact>
          </cac:Party>
       </cac:AccountingSupplierParty>
       <cac:AccountingCustomerParty>
          <cac:Party>
             ${job.invoice_einvoice_endpoint ? `<cbc:EndpointID schemeID="EM">${this.xml(job.invoice_einvoice_endpoint)}</cbc:EndpointID>` : ''}
             <cac:PostalAddress>
                <cbc:StreetName>${this.xml(job.invoice_street)}</cbc:StreetName>
                <cbc:CityName>${this.xml(job.invoice_city)}</cbc:CityName>
                <cbc:PostalZone>${this.xml(job.invoice_postcode)}</cbc:PostalZone>
                ${job.invoice_department ? `<cac:AddressLine><cbc:Line>${this.xml(job.invoice_department)}</cbc:Line></cac:AddressLine>` : ''}
                <cac:Country>
                   <cbc:IdentificationCode>${this.xml(this.normalizeCountry(job.invoice_country || invoice.country))}</cbc:IdentificationCode>
                </cac:Country>
             </cac:PostalAddress>
             <cac:PartyTaxScheme>
                <cbc:CompanyID>${this.xml(invoice.vat_id)}</cbc:CompanyID>
                <cac:TaxScheme>
                   <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
             </cac:PartyTaxScheme>
             <cac:PartyLegalEntity>
                <cbc:RegistrationName>${this.xml(job.invoice_company)}</cbc:RegistrationName>
             </cac:PartyLegalEntity>
          </cac:Party>
       </cac:AccountingCustomerParty>
       <cac:Delivery>
          ${this.normalizeDate(job.delivery_date) ? `<cbc:ActualDeliveryDate>${this.xml(this.normalizeDate(job.delivery_date))}</cbc:ActualDeliveryDate>` : ''}
          <cac:DeliveryLocation>
             <cac:Address>
                <cbc:StreetName>${this.xml(job.delivery_street)}</cbc:StreetName>
                ${job.delivery_department ? `<cbc:AdditionalStreetName>${this.xml(job.delivery_department)}</cbc:AdditionalStreetName>` : ''}
                <cbc:CityName>${this.xml(job.delivery_city)}</cbc:CityName>
                <cbc:PostalZone>${this.xml(job.delivery_postcode)}</cbc:PostalZone>
                <cac:Country>
                   <cbc:IdentificationCode>${this.xml(this.normalizeCountry(job.delivery_country || invoice.country))}</cbc:IdentificationCode>
                </cac:Country>
             </cac:Address>
          </cac:DeliveryLocation>
          <cac:DeliveryParty>
             <cac:PartyName>
                <cbc:Name>${this.xml(job.delivery_company)}</cbc:Name>
             </cac:PartyName>
          </cac:DeliveryParty>
       </cac:Delivery>
       <cac:PaymentMeans>
          <cbc:PaymentMeansCode>58</cbc:PaymentMeansCode>
          <cac:PayeeFinancialAccount>
             <cbc:ID>DE63810632380001089722</cbc:ID>
             <cbc:Name>PHM-Industrieanlagen GmbH</cbc:Name>
             <cac:FinancialInstitutionBranch>
                <cbc:ID>GENODEF1BRG</cbc:ID>
             </cac:FinancialInstitutionBranch>
          </cac:PayeeFinancialAccount>
       </cac:PaymentMeans>`;
    if (skonto > 0) {
      invoiceText += `
      <cac:PaymentTerms>
          <cbc:Note>#SKONTO#TAGE=${this.num(skontoDays, 0)}#PROZENT=${this.num(skonto)}#BASISBETRAG=${this.num(invoice.brutto)}#
          </cbc:Note>
      </cac:PaymentTerms>`;
    }
    invoiceText += `
      <cac:TaxTotal>
          <cbc:TaxAmount currencyID="EUR">${taxAmount.toFixed(2)}</cbc:TaxAmount>
          <cac:TaxSubtotal>
              <cbc:TaxableAmount currencyID="EUR">${exclusiveAmount.toFixed(2)}</cbc:TaxableAmount>
              <cbc:TaxAmount currencyID="EUR">${taxAmount.toFixed(2)}</cbc:TaxAmount>
              <cac:TaxCategory>
                  <cbc:ID>${taxCategoryId}</cbc:ID>
                  <cbc:Percent>${this.num(taxPercent)}</cbc:Percent>
                  <cac:TaxScheme>
                      <cbc:ID>VAT</cbc:ID>
                  </cac:TaxScheme>
              </cac:TaxCategory>
          </cac:TaxSubtotal>
      </cac:TaxTotal>`;
    invoiceText += `
    <cac:LegalMonetaryTotal>
        <cbc:LineExtensionAmount currencyID="EUR">${exclusiveAmount.toFixed(2)}</cbc:LineExtensionAmount>
        <cbc:TaxExclusiveAmount currencyID="EUR">${exclusiveAmount.toFixed(2)}</cbc:TaxExclusiveAmount>
        <cbc:TaxInclusiveAmount currencyID="EUR">${this.num(inclusiveAmount)}</cbc:TaxInclusiveAmount>
        <cbc:AllowanceTotalAmount currencyID="EUR">0.00</cbc:AllowanceTotalAmount>
        <cbc:ChargeTotalAmount currencyID="EUR">0.00</cbc:ChargeTotalAmount>
        <cbc:PrepaidAmount currencyID="EUR">0.00</cbc:PrepaidAmount>
        <cbc:PayableAmount currencyID="EUR">${this.num(invoice.brutto)}</cbc:PayableAmount>
    </cac:LegalMonetaryTotal>
    ${invoicePositions.join('\n')}`;
    invoiceText += `</ubl:Invoice>`;

    return invoiceText;
  }
}
